import React, { ComponentProps, ReactElement, useContext, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { SalesOrderLine } from '../../graphql/entities';
import { MenuItem } from 'react-contextmenu';
import LineDetailModal from '../../components/LineDetailModal/LineDetailModal';
import CancelDispatchInstructionOrderLine from 'graphql/mutations/sales/CancelDispatchInstructionOrderLine.graphql';
import SalesOrderLineApproveManualPriceMutation from 'graphql/mutations/sales/SalesOrderLineApproveManualPriceMutation.graphql';
import serviceActions from '../../state/modules/service/actions';
import headerAttributeQuery from '../../graphql/queries/sales/SalesAttributeConfigListQuery.graphql';
import productAttributeQuery from '../../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import customerAttributeQuery from '../../graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import Formatters from '../../components/grid-addons/formatters';
import getClientMutationId from '../../utils/getClientMutationId';
import getProductDP from '../../utils/getProductDP';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { useSalesOrderLineWorkspaceActions } from '../../actions/sales/SalesOrderLineWorkspaceAction';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import {
  AlertType,
  Direction,
  FilterOperation,
  ICancelDispatchInstructionOrderLineMutationResult,
  ICancelDispatchInstructionOrderLineMutationVariables,
  IEntityMetaData,
  IJobInfo,
  IProductWarehouseDetail,
  ISalesOrderLine,
  ISalesOrderLineApproveManualPriceMutationResult,
  ISalesOrderLineApproveManualPriceMutationVariables,
  ProductClass,
} from '../../graphql/schema/p4types';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import { useBackgroundProcess } from '../../utils/BackgroundJobs';
import ConnectedFormContext, { ConnectedFormContextType } from '../../components/Context/ConnectedFormContext';
import { Paths } from '../../@types/keyOfNested';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import ProductFormModalFormatter from '../../components/modals/formatters/ProductFormModalFormatter';
import { IFormatterProps, IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { useAbstractSalesLineColumnOverrides } from '../../utils/abstractSalesHooks';
import { useAcknowledgedDeliveryDate } from '../../components/Workspace/useAcknowledgementDeliveryDate';
import useWarehouseColumns from '../../utils/useWarehouseColumns';
import { IColumnFromArrayDetailField } from '../../utils/setColumnsFromArray';
import { maxMakeable } from '../../graphql/entities/product/Product';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { usePrecision } from '../../utils/hooks';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { useP4ToastController } from '../system/NotificationsArea';
import { useProcessAlerts } from '../../utils/EntityMetaDataUtil';
import { useCanApproveManualPrices } from '../salesLedger/tradeCounter/hooks';

const LazyCustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));

const { formUrl, type, title, workspace } = SalesOrderLine;

const SALES_ORDER_LINE_FIELD_NAMES: Array<Paths<ISalesOrderLine>> = [
  'salesOrder.orderNumber',
  'salesOrder.orderDate',
  'salesOrder.orderReference',
  'salesOrder.customer.reference',
  'product.reference',
  'description',
  'quantity',
  'grossSellingPrice',
  'discountPercentage',
  'costPrice',
  'profitMargin',
  'priceOriginDescription',
  'salesOrderLineStatus',
];

const useSalesOrderLineColumnOverrides = (): IWorkspaceColumnOverride => {
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');
  const acknowledgedDeliveryDateColumnOverride: IWorkspaceColumnOverride = useAcknowledgedDeliveryDate();
  return useMemo(
    () => ({
      'salesOrder.orderNumber': {
        name: 'Order',
        width: 100,
      },
      'salesOrder.orderDate': {
        name: 'Order Date',
      },
      'salesOrder.customer.reference': {
        name: 'Customer',
        formatter: ModalFormatter,
        options: {
          modalContent: LazyCustomerForm,
          modalProps: { forceReadOnly: true, zIndex: 1000000 },
          entityId: 'salesOrder.customer.id',
        },
      },
      'product.reference': {
        name: 'Product',
        formatter: ProductFormModalFormatter,
        options: {
          entityId: 'product.id',
          modalProps: { zIndex: 1000000 },
        },
      },
      quantity: {
        name: 'Quantity',
        width: 100,
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      discountPercentage: {
        name: 'Discount Percentage',
        formatter: 'numeric',
      },
      priceOriginDescription: {
        name: 'Price Origin',
      },
      salesOrderLineStatus: {
        name: 'Status',
        sortable: false,
      },
      allocated: {
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      inProduction: {
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      productionRequired: {
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      dispatched: {
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      invoiced: {
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      returned: {
        formatter: 'numeric',
        options: {
          format: { decimals: getProductDP },
        },
      },
      freeStock: {
        name: 'Warehouse Free Stock',
        formatter: 'numeric',
        sortable: false,
        options: {
          format: { decimals: getProductDP },
        },
      },
      sellingUnitQuantity: {
        formatter: 'numeric',
        sortable: false,
        options: {
          format: { decimals: getProductDP },
        },
      },
      targetDate: {
        name: 'Expected Delivery Date',
      },
      priceApproved: {
        name: 'Price Approved',
        formatter: Formatters.BooleanFormatter,
      },
      'salesOrder.targetDate': {
        name: 'Sales Order - Expected Delivery Date',
      },
      linePackagingVolume: {
        formatter: Formatters.NumericFormatter,
        options: { format: { decimals: 9 }, suppressTrailingZeros: true, suppressZeroes: true },
      },
      'salesOrder.paymentMethod.description': {
        name: 'Sales Order - Payment Method',
      },
      'salesOrder.paymentTerm.description': {
        name: 'Sales Order - Payment Terms',
      },
      'salesOrder.cashStatus': {
        formatter: Formatters.EnumFormatter,
        options: fieldMetaData.CashStatus,
      },
      'product.vatProductCategory.description': {
        name: 'VAT Product Category - Description',
      },
      'product.mainSupplierDetails.supplier.name': {
        name: 'Main Supplier',
        sortable: false,
      },
      'product.mainSupplierDetails.partNumber': {
        name: 'Main Supplier Part Number',
        sortable: false,
      },
      'salesOrder.customerContact.name.fullName': {
        name: 'Delivery Contact Name',
      },
      profitMargin: {
        totalCalculation:
          '[lineProfitValueLessOverallDiscount] / [nettLineValueExcludingVatAndLessOverallDiscount] * 100',
      },
      latestMaxoptraOrder: {
        sortable: true,
      },
      'latestMaxoptraOrder.maxoptraOrderStatus': {
        name: 'Maxoptra Order Status',
        sortable: true,
      },
      'latestMaxoptraOrder.reference': {
        name: 'Maxoptra Order Reference',
        sortable: true,
      },
      'latestMaxoptraOrder.assignedDriverReference': {
        name: 'Assigned Driver Reference',
        sortable: true,
      },
      'latestMaxoptraOrder.assignedDriverName': {
        name: 'Assigned Driver Name',
        sortable: true,
      },
      'latestMaxoptraOrder.assignedVehicleReference': {
        name: 'Assigned Vehicle Reference',
        sortable: true,
      },
      'latestMaxoptraOrder.assignedVehicleName': {
        name: 'Assigned Vehicle Name',
        sortable: true,
      },
      'latestMaxoptraOrder.plannedArrivalTime': {
        name: 'Planned Arrival Time',
        sortable: true,
      },
      'latestMaxoptraOrder.plannedCompletionTime': {
        name: 'Planned Completion Time',
        sortable: true,
      },
      'latestMaxoptraOrder.stopNumber': {
        name: 'Stop Number',
        sortable: true,
      },
      'latestMaxoptraOrder.totalStopsInRun': {
        name: 'Total Stops In Run',
        sortable: true,
      },
      'latestMaxoptraOrder.runNumber': {
        name: 'Run Number',
        sortable: true,
      },
      'latestMaxoptraOrder.runReference': {
        name: 'Run Reference',
        sortable: true,
      },
      'latestMaxoptraOrder.eta': {
        name: 'ETA',
        sortable: true,
      },
      'latestMaxoptraOrder.factArrivalTimeGPS': {
        name: 'Arrival Time GPS',
        sortable: true,
      },
      'latestMaxoptraOrder.factCompletionTimeGPS': {
        name: 'Completion Time GPS',
        sortable: true,
      },
      'latestMaxoptraOrder.factArrivalTimeReported': {
        name: 'Arrival Time Reported',
        sortable: true,
      },
      'latestMaxoptraOrder.factCompletionTimeReported': {
        name: 'Completion Time Reported',
        sortable: true,
      },
      'latestMaxoptraOrder.failReason': {
        name: 'Fail Reason',
        sortable: true,
      },
      'latestMaxoptraOrder.failComment': {
        name: 'Fail Comment',
        sortable: true,
      },
      'product.windsorFrameworkAtRiskStatus': {
        name: 'Product - Windsor Framework Status',
        formatter: Formatters.EnumFormatter,
        options: fieldMetaData.WindsorFrameworkAtRiskStatus.enumValues,
      },
      ...acknowledgedDeliveryDateColumnOverride,
      ...getPricePrecisionColumnOverrides(costPricePrecision, sellingPricePrecision),
    }),
    [acknowledgedDeliveryDateColumnOverride, costPricePrecision, sellingPricePrecision]
  );
};

const warehouseStockFormatDecimals = (rowData: ISalesOrderLine): number => rowData?.product?.precision ?? 2;

const STOCK_FIELDS: Array<IColumnFromArrayDetailField> = [
  {
    title: 'Free Stock',
    fieldName: 'freeStock',
    formatter: 'numeric',
    options: { format: { decimals: warehouseStockFormatDecimals } },
  },
  {
    title: 'Makeable',
    fieldName: 'makeable',
    formatter: 'numeric',
    options: {
      infinityLimit: maxMakeable,
      format: { decimals: warehouseStockFormatDecimals },
    },
  },
];

const getPricePrecisionColumnOverrides = (
  costPricePrecision: number,
  sellingPricePrecision: number
): IWorkspaceColumnOverride => {
  return {
    'product.sellingPrices.minimumPrice': {
      name: 'Product - Minimum Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    grossSellingPrice: {
      name: 'Selling Price',
      formatter: 'numeric',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitGrossSellingPrice: {
      name: 'Single Unit Selling Price',
      formatter: 'numeric',
      options: { format: { decimals: sellingPricePrecision } },
    },
    nettSellingPrice: {
      name: 'Nett Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    profitValue: {
      name: 'Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitProfitValue: {
      name: 'Single Unit Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    haggleProfitValue: {
      name: 'Haggle Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitHaggleProfitValue: {
      name: 'Single Unit Haggle Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    costPrice: {
      name: 'Cost Price',
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
    },
    singleUnitCostPrice: {
      name: 'Single Unit Cost Price',
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
    },
    haggleCostPrice: {
      name: 'Haggle Cost',
      options: { format: { decimals: costPricePrecision } },
    },
    singleUnitHaggleCostPrice: {
      name: 'Single Unit Haggle Cost',
      options: { format: { decimals: costPricePrecision } },
    },
  };
};

export const createWarehouseLevelFormatter = (columnName: string, fieldName: string) => {
  return {
    name: columnName,
    sortable: false,
    formatter: ({ dependentValues }: IFormatterProps<ISalesOrderLine>) => {
      const rowData = dependentValues.rowData;
      const productWarehouseDetails = rowData?.product?.productWarehouseDetails ?? [];
      const thisWarehouseDetail = productWarehouseDetails.find((pwd) => pwd?.warehouse?.id === rowData.warehouse?.id);

      if (thisWarehouseDetail) {
        const warehouseDetailValue = thisWarehouseDetail[fieldName as keyof IProductWarehouseDetail];
        return !warehouseDetailValue ? 0 : warehouseDetailValue;
      }
      return 0;
    },
  };
};

const SalesOrderLineWorkspaceActions = (
  props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'actionButtons'>
) => {
  const [salesOrderLineWorkspaceActions, modals] = useSalesOrderLineWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={salesOrderLineWorkspaceActions} />
    </>
  );
};

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  const fieldName = column?.fieldName;
  return (
    fieldName !== 'warehouseMinimumLevel' &&
    fieldName !== 'warehouseMaximumLevel' &&
    fieldName !== 'warehouseOptimumLevel'
  );
};

const SalesOrderLineWorkspace = ({ fieldNames = [], ...props }: any) => {
  const { additionalFieldNames = [], customColumns: additionalColumns } = props;
  const [detailModalState, setDetailModalState] = useState({ modalOpen: false, selectedId: null });
  const dispatch = useDispatch();
  const p4ToastController = useP4ToastController();
  const [cancelDispatch] = useMutation<
    ICancelDispatchInstructionOrderLineMutationResult,
    ICancelDispatchInstructionOrderLineMutationVariables
  >(CancelDispatchInstructionOrderLine);
  const [approveManualPrice] = useMutation<
    ISalesOrderLineApproveManualPriceMutationResult,
    ISalesOrderLineApproveManualPriceMutationVariables
  >(SalesOrderLineApproveManualPriceMutation);
  const processAlerts = useProcessAlerts();
  const workspaceRef = useRef<any>();
  //This isn't necessarily nested below a connectedForm, so casting to Partial.
  const connectedForm = useContext(ConnectedFormContext) as Partial<ConnectedFormContextType>;
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');
  const canApproveManualPrices = useCanApproveManualPrices();

  const runCancelDispatchBackgroundProcess = useBackgroundProcess({
    title: 'Cancel Dispatch',
    onComplete: (job) => {
      dispatch(serviceActions.genericAlert('Dispatch cancelled', `Dispatch has been cancelled`, AlertType.Success));
      workspaceRef.current.reloadData();
      connectedForm?.reloadData?.(true);
    },
  });

  const headerAttributeColumns = useAttributesForWorkspace(headerAttributeQuery, {
    columnKey: 'salesOrder.attributeValues.attributeConfig.title',
    valuesPath: 'salesOrder.attributeValues',
  });

  const productAttributeColumns = useAttributesForWorkspace(productAttributeQuery, {
    valuesPath: 'product.attributeValues',
    columnKey: 'product.attributeValues.attributeConfig.title',
  });

  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'salesOrder.customer.attributeValues',
    columnKey: 'salesOrder.customer.attributeValues.attributeConfig.title',
  });

  const warehouseColumns = useWarehouseColumns('product.productWarehouseDetails', STOCK_FIELDS);

  const getLineContextButtons = ({ rowData }: { rowData: ISalesOrderLine }) => {
    const renderedButtons: Array<Array<ReactElement>> = [];
    if (canApproveManualPrices) {
      renderedButtons.push([
        <MenuItem
          key="approveManualPrice"
          onClick={(e) => {
            e.preventDefault();
            const variables = {
              clientMutationId: getClientMutationId(),
              salesOrderLineId: rowData?.id,
            };
            approveManualPrice({ variables: { input: variables } }).then(
              (response) => {
                p4ToastController.dispatchSimpleToast(
                  'Manual Price Approved',
                  `Manual price has been approved`,
                  AlertType.Success
                );
                processAlerts(response?.data?.data?.result?.entityMetaData as IEntityMetaData);

                workspaceRef.current.reloadData();
                connectedForm?.reloadData?.(true);
              },
              (err) => handleComponentQueryError(err, { dispatch, name: 'Sales Order Line', entity: SalesOrderLine })
            );
          }}
          disabled={rowData?.priceApproved ?? false}
        >
          Approve Manual Price
        </MenuItem>,
      ]);
    }

    renderedButtons.push([
      <MenuItem
        key={'actionButtonDisplayHistory'}
        onClick={(e) => {
          e.preventDefault();
          setDetailModalState({ modalOpen: true, selectedId: rowData.id });
        }}
      >
        Line History/Audit
      </MenuItem>,
      <MenuItem
        key="cancelDispatch"
        onClick={(e) => {
          e.preventDefault();
          const variables = {
            clientMutationId: getClientMutationId(),
            salesOrderLine: {
              id: rowData.id,
            },
          };
          cancelDispatch({ variables: { input: variables } }).then(
            (result) => {
              const job = result?.data?.cancelDispatchInstructionOrderLine?.result as IJobInfo;
              runCancelDispatchBackgroundProcess(job);
            },
            (err) => handleComponentQueryError(err, { dispatch, name: 'Sales Order Line', entity: SalesOrderLine })
          );
        }}
      >
        Cancel Dispatch
      </MenuItem>,
    ]);

    return renderedButtons;
  };

  const lineEventModalProps = {
    ...props,
    viewScreenName: '/sales/SalesOrderLineEvent/Workspace',
  };

  const customColumns = useMemo(() => {
    const minimumLevelColumn = createWarehouseLevelFormatter('Stock Min', 'minimumLevel');
    const maximumLevelColumn = createWarehouseLevelFormatter('Stock Max', 'maximumLevel');
    const optimumLevelColumn = createWarehouseLevelFormatter('Stock Opt', 'optimumLevel');

    return {
      ...headerAttributeColumns,
      ...productAttributeColumns,
      ...customerAttributeColumns,
      ...additionalColumns,
      ...warehouseColumns,
      warehouseMinimumLevel: minimumLevelColumn,
      warehouseMaximumLevel: maximumLevelColumn,
      warehouseOptimumLevel: optimumLevelColumn,
    };
  }, [headerAttributeColumns, productAttributeColumns, customerAttributeColumns, additionalColumns, warehouseColumns]);

  const columnOverrides: IWorkspaceColumnOverride = useSalesOrderLineColumnOverrides();

  const salesLineColumnOverrides = useAbstractSalesLineColumnOverrides(
    columnOverrides,
    costPricePrecision,
    sellingPricePrecision
  );

  const columns = useMemo(() => {
    return props.columnData || [...SALES_ORDER_LINE_FIELD_NAMES, ...fieldNames, ...(additionalFieldNames ?? [])];
  }, [additionalFieldNames, fieldNames, props.columnData]);

  const workspaceProps = useWorkspace<ISalesOrderLine>(props, SalesOrderLine, {
    columns: columns,
    columnOverrides: salesLineColumnOverrides,
    customColumns: customColumns,
    initialSortColumn: props.initialSortColumn || 'salesOrder.orderNumber',
    initialSortDirection: props.initialSortDir || Direction.Desc,
    filters: [
      {
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'product.productClass',
          value: ProductClass.FreeType,
        },
      },
    ],
  });

  return (
    <>
      <Workspace
        ref={workspaceRef}
        entityType={type}
        formIdOverride={`salesOrder.id`}
        title={title}
        formUrl={props.noForm ? null : formUrl}
        defaultQuery={workspace}
        contextKey={'SOL_Workspace'}
        renderActionButtons={getLineContextButtons}
        {...workspaceProps}
        {...props}
        entity={SalesOrderLine}
        workspaceActionButtonsHOC={SalesOrderLineWorkspaceActions}
        availableFiltersFilter={availableFiltersFilter}
      />
      <LineDetailModal
        formConfig={{}}
        updateModalOpen={() => {
          setDetailModalState({ modalOpen: false, selectedId: detailModalState.selectedId });
        }}
        modalOpen={detailModalState.modalOpen}
        selectedId={detailModalState.selectedId}
        {...lineEventModalProps}
      />
    </>
  );
};

export default SalesOrderLineWorkspace;
